import React, { lazy, Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
// import { SwitchTransition, CSSTransition } from 'react-transition-group'
import { connect } from 'react-redux'
import Layout from 'layouts'

const routes = [
  // Account
  {
    path: '/apps',
    Component: lazy(() => import('pages/account/apps')),
  },
  {
    path: '/settings',
    Component: lazy(() => import('pages/account/settings')),
  },
  {
    path: '/billing',
    Component: lazy(() => import('pages/account/billing')),
  },
  {
    path: '/create-application',
    Component: lazy(() => import('pages/app/create-application')),
  },
  {
    path: '/create-your-first-application',
    Component: lazy(() => import('pages/auth/create-application')),
  },
  {
    path: '/schedule-your-kick-off-call',
    Component: lazy(() => import('pages/auth/schedule-kick-off-call')),
  },

  // app modules
  {
    path: '/app/:appID/restrictedPage',
    exact: true,
    Component: lazy(() => import('pages/app/restrictedPage/index')),
  },
  {
    path: '/app/:appID/quick-start',
    exact: true,
    Component: lazy(() => import('pages/app/quick-start/index')),
  },
  {
    path: '/app/:appID/credentials',
    Component: lazy(() => import('pages/app/credentials')),
  },
  {
    path: '/app/:appID/users',
    Component: lazy(() => import('pages/app/users')),
  },
  {
    path: '/app/:appID/members',
    Component: lazy(() => import('pages/app/members')),
  },
  {
    path: '/app/:appID/roles',
    Component: lazy(() => import('pages/app/roles')),
  },
  {
    path: '/app/:appID/custom-tabs',
    Component: lazy(() => import('pages/app/custom-tabs')),
  },
  {
    path: '/app/:appID/groups',
    Component: lazy(() => import('pages/app/groups')),
  },
  {
    path: '/app/:appID/agents',
    Component: lazy(() => import('pages/app/bots')),
  },
  {
    path: '/app/:appID/ai-agent/get-started',
    Component: lazy(() => import('pages/app/bots/components/get-started')),
  },
  {
    path: '/app/:appID/byo-agent/get-started',
    Component: lazy(() => import('pages/app/bots/components/get-started')),
  },
  {
    path: '/app/:appID/third-party-ai-agents/add',
    Component: lazy(() => import('pages/app/bots/components/ai-agents/forms/add')),
  },
  {
    path: '/app/:appID/third-party-ai-agents/:uid/edit',
    Component: lazy(() => import('pages/app/bots/components/ai-agents/forms/edit')),
  },
  {
    path: '/app/:appID/ai-actions/add',
    Component: lazy(() => import('pages/app/bots/components/ai-actions/forms/add')),
  },
  {
    path: '/app/:appID/ai-actions/:uid/edit',
    Component: lazy(() => import('pages/app/bots/components/ai-actions/forms/edit')),
  },
  {
    path: '/app/:appID/ai-tools/add',
    Component: lazy(() => import('pages/app/bots/components/ai-tools/forms/add')),
  },
  {
    path: '/app/:appID/ai-tools/:uid/edit',
    Component: lazy(() => import('pages/app/bots/components/ai-tools/forms/edit')),
  },
  {
    path: '/app/:appID/ai-actions',
    Component: lazy(() => import('pages/app/bots/components/third-party-ai-agents-tabs')),
  },
  {
    path: '/app/:appID/ai-tools',
    Component: lazy(() => import('pages/app/bots/components/third-party-ai-agents-tabs')),
  },
  {
    path: '/app/:appID/third-party-ai-agents',
    Component: lazy(() => import('pages/app/bots/components/third-party-ai-agents-tabs')),
  },


  // Agent Configuration Routes - Must come BEFORE /app/:appID/ai-agents to prevent route conflicts
  {
    path: '/app/:appID/ai-agents/:agentUid',
    exact: true,
    Component: () => <Redirect to={`${window.location.pathname}/instructions`} />,
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/overview',
    Component: lazy(() => import('pages/app/agent-config/overview')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/instructions',
    Component: lazy(() => import('pages/app/agent-config/instructions')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/tool/:toolName/actions',
    Component: lazy(() => import('pages/app/agent-config/tool/action-list')),
    exact: true,
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/tool/custom-api-tool',
    Component: lazy(() => import('pages/app/agent-config/tool/custom-api-tool')),
    exact: true,
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/tool',
    Component: lazy(() => import('pages/app/agent-config/tool')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/mcp',
    Component: lazy(() => import('pages/app/agent-config/mcp')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/deploy/:builderId',
    Component: lazy(() => import('pages/app/builder/index')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/deploy',
    Component: lazy(() => import('pages/app/agent-config/deploy')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/pricing',
    Component: lazy(() => import('pages/app/ai-agents-pricing')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/knowledge-base',
    Component: lazy(() => import('pages/app/agent-config/knowledge-base')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/variables',
    Component: lazy(() => import('pages/app/agent-config/variables')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/tasks',
    Component: lazy(() => import('pages/app/agent-config/tasks')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/test',
    Component: lazy(() => import('pages/app/agent-config/test')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/logs',
    Component: lazy(() => import('pages/app/agent-config/logs')),
  },
  {
    path: '/app/:appID/ai-agents/:agentUid/analyse',
    Component: lazy(() => import('pages/app/agent-config/analyse')),
  },
  
  // AI Agents List - Must come AFTER all specific agent routes
  {
    path: '/app/:appID/ai-agents',
    Component: lazy(() => import('pages/app/bots/components/ai-agent-builders')),
  },  {
    path: '/app/:appID/webhooks-legacy',
    Component: lazy(() => import('pages/app/webhooks-legacy')),
  },
  {
    path: '/app/:appID/webhooks',
    Component: lazy(() => import('pages/app/webhooks')),
  },

  {
    path: '/app/:appID/general',
    Component: lazy(() => import('pages/app/settings')),
  },
  {
    path: '/app/:appID/plans-billing',
    Component: lazy(() => import('pages/app/plans-billing')),
  },
  {
    path: '/app/:appID/analytics',
    Component: lazy(() => import('pages/app/analytics')),
  },
  {
    path: '/app/:appID/ai-features',
    Component: lazy(() => import('pages/app/ai-features')),
  },
  // Uncomment these lines if AI settings and bots are required in future.
  // {
  //   path: '/app/:appID/ai-settings',
  //   Component: lazy(() => import('pages/app/ai-settings')),
  // },
  {
    path: '/app/:appID/ai-bots',
    Component: lazy(() => import('pages/app/ai-bots')),
  },
  {
    path: '/app/:appID/ai-instructions',
    Component: lazy(() => import('pages/app/ai-personalities')),
  },
  {
    path: '/app/:appID/overview',
    Component: lazy(() => import('pages/app/statistics')),
  },
  {
    path: '/app/:appID/messages',
    Component: lazy(() => import('pages/app/messages')),
  },
  {
    path: '/app/:appID/chats',
    Component: lazy(() => import('pages/app/messages')),
  },
  {
    path: '/app/:appID/messaging-get-started',
    Component: lazy(() => import('pages/app/messages/get-started')),
  },
  {
    path: '/app/:appID/chat-features',
    Component: lazy(() => import('pages/app/messages/features')),
  },
  {
    path: '/app/:appID/chat-settings',
    Component: lazy(() => import('pages/app/messages/settings')),
  },
  {
    path: '/app/:appID/widgets',
    Component: lazy(() => import('pages/app/chat-widgets')),
  },
  {
    path: '/app/:appID/notifications-get-started',
    Component: lazy(() => import('pages/app/notifications/push-notification/get-started')),
  },
  {
    path: '/app/:appID/notifications-logs',
    Component: lazy(() => import('pages/app/notifications/push-notification/logs')),
  },
  {
    path: '/app/:appID/notifications',
    Component: lazy(() => import('pages/app/notifications')),
  },
  {
    path: '/app/:appID/notifications-legacy',
    Component: lazy(() => import('pages/app/notifications/push-notification/notifications-legacy')),
  },
  {
    path: '/app/:appID/moderation',
    Component: lazy(() => import('pages/app/moderation')),
  },
  {
    path: '/app/:appID/moderation/:tabKey',
    Component: lazy(() => import('pages/app/moderation')),
  },
  {
    path: '/app/:appID/group-moderation',
    Component: lazy(() => import('pages/app/group-moderation')),
  },
  // Mod messages
  {
    path: '/app/:appID/messages',
    Component: lazy(() => import('pages/app/messages/moderation')),
  },
  // calls
  {
    path: '/app/:appID/calls',
    Component: lazy(() => import('pages/app/calls/index')),
  },
  {
    path: '/app/:appID/calling-get-started',
    Component: lazy(() => import('pages/app/calls/get-started')),
  },
  // calls-legacy
  {
    path: '/app/:appID/calls-legacy',
    Component: lazy(() => import('pages/app/calls_legacy/index')),
  },
  {
    path: '/app/:appID/builder/:builderId',
    Component: lazy(() => import('pages/app/builder/index')),
  },
  {
    path: '/app/:appID/campaigns',
    Component: lazy(() => import('pages/app/campaigns/index')),
  },
  // {
  //   path: '/app/:appID/integrate',
  //   Component: lazy(() => import('pages/app/integrate/index')),
  // },
  // System Pages
  {
    path: '/cli-auth/success',
    Component: lazy(() => import('pages/auth/cliAuthSuccess')),
  },
  {
    path: '/login',
    Component: lazy(() => import('pages/auth/login')),
  },
  {
    path: '/signup',
    Component: lazy(() => import('pages/auth/signup')),
  },
  {
    path: '/register',
    Component: lazy(() => import('pages/auth/signup')),
  },
  {
    path: '/verify-account',
    Component: lazy(() => import('pages/auth/verify-otp')),
  },
  {
    path: '/token/:id',
    Component: lazy(() => import('pages/auth/loginUsingToken')),
  },
  {
    path: '/forgot-password',
    Component: lazy(() => import('pages/auth/forgot')),
  },
  {
    path: '/reset/password/:id',
    Component: lazy(() => import('pages/auth/resetPassword')),
  },
  {
    path: '/reset-password-email-sent',
    Component: lazy(() => import('pages/auth/resetPasswordEmailSent')),
  },
  {
    path: '/verify/email/:id/:email',
    Component: lazy(() => import('pages/auth/welcome')),
  },
  {
    path: '/choose-role',
    Component: lazy(() => import('pages/auth/welcome/Role')),
  },
  {
    path: '/choose-intent',
    Component: lazy(() => import('pages/auth/welcome/Intent')),
  },
  {
    path: '/verification-email-sent',
    Component: lazy(() => import('pages/auth/verificationEmailSent')),
  },
  {
    path: '/invite/collaborator/:id',
    Component: lazy(() => import('pages/auth/acceptCollboratorInvitation')),
  },
  {
    path: '/error/:id',
    Component: lazy(() => import('pages/error')),
  },
  {
    path: '/app/:appID/quick-start/pick-a-platform',
    exact: true,
    Component: lazy(() => import('pages/app/quick-start/pick-a-platform')),
  },
  {
    path: '/app/:appID/quick-start/pick-a-platform/ios',
    exact: true,
    Component: lazy(() => import('pages/app/quick-start/pick-a-platform/ios')),
  },
  {
    path: '/app/:appID/quick-start/pick-a-platform/android',
    Component: lazy(() => import('pages/app/quick-start/pick-a-platform/android')),
  },
  {
    path: '/app/:appID/quick-start/pick-a-platform/react',
    Component: lazy(() => import('pages/app/quick-start/pick-a-platform/react')),
  },
  {
    path: '/app/:appID/quick-start/pick-a-platform/react-native',
    Component: lazy(() => import('pages/app/quick-start/pick-a-platform/react-native')),
  },
  {
    path: '/app/:appID/quick-start/pick-a-platform/angular',
    Component: lazy(() => import('pages/app/quick-start/pick-a-platform/angular')),
  },
]

const mapStateToProps = ({ apps, settings }) => ({ apps, settings })

@connect(mapStateToProps)
class Router extends React.Component {
  // set route match state globally for accessing it in <Layout /> components
  setGlobalRouteMatchState = match => {
    const { dispatch } = this.props
    dispatch({
      type: 'menu/CHANGE_SETTING',
      payload: {
        setting: 'globalRouteMatch',
        value: match,
      },
    })
  }

  render() {
    const { history } = this.props

    return (
      <ConnectedRouter history={history}>
        <Layout>
          <Route
            render={state => {
              const { location } = state
              return (
                // <SwitchTransition>
                //   <CSSTransition
                //     key={location.pathname}
                //     appear
                //     classNames="slide-fadein-up"
                //     timeout={300}
                //   >
                <Switch location={location}>
                  {/* <Route exact path="/" render={() => <Redirect to="/apps" />} /> */}
                  <Route
                    exact
                    path="/"
                    render={() => <Redirect to={{ pathname: '/apps', defaultpath: true }} />}
                  />

                  {routes.map(({ path, Component, exact }) => (
                    <Route
                      path={path}
                      key={path}
                      exact={exact}
                      render={props => {
                        this.setGlobalRouteMatchState(props.match)
                        return (
                          <div className="slide-fadein-up">
                            <Suspense fallback={null}>
                              <Component />
                            </Suspense>
                          </div>
                        )
                      }}
                    />
                  ))}
                  <Redirect to="/error/404" />
                </Switch>
              )
            }}
          />
        </Layout>
      </ConnectedRouter>
    )
  }
}

export default Router
